import { graphql, PageProps } from 'gatsby'
import React, { useContext, useEffect, useState } from 'react'

import ClientBar from '../components/ClientBar'
import ContactForm from '../components/ContactForm'
import FilterableProductList from '../components/FilterableProductList'
import Metadata from '../components/Metadata'
import Page from '../components/Page'
import ProductFilters from '../components/ProductFilters'
import Section from '../components/section/Section'
import { useFilteredProducts } from '../hooks/useFilteredProducts'
import useProducts from '../hooks/useProducts'
import { LocaleContext } from '../locale/i18n'
import { categoryLandingSlugs, staticPageSlugs } from '../locale/pathing'
import {
  DeviceType,
  IndustryOrMaterial,
  industryOrMaterialValues,
  TypeOfTesting,
  typeOfTestingValues,
} from '../logic/catalog'
import { filterLocale } from '../logic/contentful'
import { ContentGroups } from '../logic/ga4'
import { IContentfulCategoryLanding } from '../logic/shared-types'
import { standardToString } from '../logic/standard'
import { StaticPage } from '../logic/staticPage'

import './products.scss'

export type ProductListProps = PageProps<
  {
    categoryPages: {
      nodes: IContentfulCategoryLanding[]
    }
    deviceTypes: {
      nodes: {
        deviceTypeData: DeviceType
      }[]
    }
  },
  never
>

const landingPageKey = (
  activeIndustry: IndustryOrMaterial | null,
  activeTypeOfTesting: TypeOfTesting | null,
) => {
  if (activeIndustry) {
    return activeIndustry
  }
  if (activeTypeOfTesting) {
    return activeTypeOfTesting
  }
}

const ProductList = ({ data }: ProductListProps) => {
  const { locale, t } = useContext(LocaleContext)
  const allProducts = useProducts()

  const { availableFilters, filters, handleChange, products } =
    useFilteredProducts({
      products: allProducts.map(p => ({
        ...p,
        standardNames: p.standards.map(standardToString),
      })),
    })

  const deviceTypes = data.deviceTypes.nodes.map(node => node.deviceTypeData)

  const IndustriesAndTypesOfTesting = allProducts.map(p => ({
    industryOrMaterial: p.industryOrMaterial,
    typeOfTesting: p.typeOfTesting,
  }))

  const industries = industryOrMaterialValues
    .filter(industry =>
      IndustriesAndTypesOfTesting.some(p =>
        p.industryOrMaterial.includes(industry),
      ),
    )
    .sort((a, b) =>
      t(`industryOrMaterial.${a}`) > t(`industryOrMaterial.${b}`) ? 1 : -1,
    )

  const typesOfTesting = typeOfTestingValues
    .filter(typeOfTesting =>
      IndustriesAndTypesOfTesting.some(p =>
        p.typeOfTesting.includes(typeOfTesting),
      ),
    )
    .sort((a, b) =>
      t(`typeOfTesting.${a}`) > t(`typeOfTesting.${b}`) ? 1 : -1,
    )

  const key = landingPageKey(
    filters.industryOrMaterial.value,
    filters.typeOfTesting.value,
  )
  const localePages = filterLocale(data.categoryPages.nodes, locale)
  const rootLandingProps = {
    categoryLandingPageNode: localePages
      // default keyword is reserved for default hero in contentful
      .find(node => node.key === 'default'),
    localisedSlugs: staticPageSlugs(StaticPage.PRODUCTS),
  }
  const [{ localisedSlugs, categoryLandingPageNode }, setLandingProps] =
    useState(rootLandingProps)
  useEffect(() => {
    if (key) {
      setLandingProps({
        categoryLandingPageNode: filterLocale(
          data.categoryPages.nodes,
          locale,
        ).find(node => key === node.key),
        localisedSlugs: categoryLandingSlugs(key),
      })
      return
    }
    setLandingProps(rootLandingProps)
  }, [filters])

  return (
    <Page
      contentGroup={ContentGroups.productsPage}
      localisedSlugs={localisedSlugs}
    >
      <Metadata
        title={categoryLandingPageNode?.metaTitle}
        description={
          categoryLandingPageNode?.metaDescription?.metaDescription ||
          categoryLandingPageNode?.intro?.intro ||
          t('metadata.products.description')
        }
      />
      <div className="productList">
        <div className="productList__hero center">
          <div className="productList__heroContent">
            <h1 className="productList__heading">
              {categoryLandingPageNode?.title}
            </h1>
            <span>{categoryLandingPageNode?.intro?.intro}</span>
          </div>
          <ProductFilters
            availableFilters={availableFilters}
            deviceTypes={deviceTypes}
            filters={filters}
            onChange={handleChange}
          />
        </div>
        <div className="gutter gutter--center">
          <FilterableProductList
            filters={filters}
            onFilterChange={handleChange}
            products={products}
            industryOrMaterialValues={industries}
            typeOfTestingValues={typesOfTesting}
          />
        </div>
      </div>
      <Section backgroundColor="light" id="askForOffer">
        <ContactForm />
        <br />
        <ClientBar />
      </Section>
    </Page>
  )
}

export const query = graphql`
  query ($locale: String) {
    categoryPages: allContentfulCategoryLandingPage {
      nodes {
        contentful_id
        node_locale
        metaTitle
        metaDescription {
          metaDescription
        }
        intro {
          intro
        }
        key
        title
      }
    }
    deviceTypes: allDeviceType(filter: { locale: { eq: $locale } }) {
      nodes {
        deviceTypeData {
          deviceTypeId
          nameLong
          nameShort
        }
      }
    }
  }
`

export default ProductList
